import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Button,
  Heading,
  Input,
  LoadingSpinner,
  Select,
  SortableTableHeader,
  Text,
} from '../components/ui'
import { FunnelIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { BRAND_COLOR } from '../constants'
import { supabase } from '../lib/supabase'
import { portaisAtendimentoService } from '../services/portaisAtendimento'
import {
  helpdeskUserLogsService,
  type HelpdeskLogWithUser,
} from '../services/helpdeskUserLogs'

const ITEMS_PER_PAGE = 20

function getLastMonthDates() {
  const hoje = new Date()
  const inicio = new Date(hoje.getFullYear(), hoje.getMonth() - 1, hoje.getDate())
  const formatDate = (date: Date) => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }
  return { inicio: formatDate(inicio), fim: formatDate(hoje) }
}

function localCalendarDayStartIso(yyyyMmDd: string): string {
  const parts = yyyyMmDd.split('-').map((p) => parseInt(p, 10))
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) {
    return new Date(yyyyMmDd).toISOString()
  }
  const [y, m, d] = parts
  return new Date(y, m - 1, d, 0, 0, 0, 0).toISOString()
}

function localCalendarDayEndIso(yyyyMmDd: string): string {
  const parts = yyyyMmDd.split('-').map((p) => parseInt(p, 10))
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) {
    const t = new Date(yyyyMmDd)
    t.setHours(23, 59, 59, 999)
    return t.toISOString()
  }
  const [y, m, d] = parts
  return new Date(y, m - 1, d, 23, 59, 59, 999).toISOString()
}

function formatDate(dateString: string): string {
  const date = new Date(dateString)
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date)
}

function translateAction(action: string): string {
  const translations: Record<string, string> = {
    create: 'Criar',
    update: 'Atualizar',
    delete: 'Excluir',
    view: 'Visualizar',
    export: 'Exportar',
    import: 'Importar',
    login: 'Login',
    logout: 'Logout',
    other: 'Outro',
  }
  return translations[action] || action
}

const PortaisAtendimentoUserActivityLogsTab: React.FC = () => {
  const lastMonthDates = getLastMonthDates()

  const [logs, setLogs] = useState<HelpdeskLogWithUser[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [total, setTotal] = useState(0)
  const [hasSearched, setHasSearched] = useState(false)
  const [isHelpdeskAdmin, setIsHelpdeskAdmin] = useState(false)
  const [currentUserId, setCurrentUserId] = useState<string | null>(null)

  const [userIdFilter, setUserIdFilter] = useState('')
  const [entityTypeFilter, setEntityTypeFilter] = useState<string>('all')
  const [dateFrom, setDateFrom] = useState(lastMonthDates.inicio)
  const [dateTo, setDateTo] = useState(lastMonthDates.fim)
  const [searchTerm, setSearchTerm] = useState('')

  const [currentPage, setCurrentPage] = useState(1)
  const [sortBy, setSortBy] = useState<
    'created_at' | 'user_name' | 'action' | 'entity_type' | 'description'
  >('created_at')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')

  const [selectedLog, setSelectedLog] = useState<HelpdeskLogWithUser | null>(null)
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false)

  const [users, setUsers] = useState<Array<{ id: string; name: string | null }>>([])
  const [availableEntityTypes, setAvailableEntityTypes] = useState<string[]>([])
  const [loadingEntityTypes, setLoadingEntityTypes] = useState(false)

  useEffect(() => {
    const loadContext = async () => {
      try {
        const [admin, authResult] = await Promise.all([
          portaisAtendimentoService.isCurrentUserHelpdeskAdmin(),
          supabase.auth.getUser(),
        ])
        setIsHelpdeskAdmin(admin)
        setCurrentUserId(authResult.data.user?.id ?? null)
      } catch (err) {
        console.error('Erro ao carregar contexto de logs:', err)
      }
    }
    void loadContext()
  }, [])

  useEffect(() => {
    if (!isHelpdeskAdmin) return

    const loadUsers = async () => {
      try {
        const { data, error: usersError } = await supabase
          .from('profiles')
          .select('id, name')
          .not('helpdesk_user_group_id', 'is', null)
          .order('name')
          .limit(200)

        if (usersError) throw usersError
        setUsers(data || [])
      } catch (err) {
        console.error('Erro ao carregar usuários do helpdesk:', err)
      }
    }

    void loadUsers()
  }, [isHelpdeskAdmin])

  const loadAvailableEntityTypes = useCallback(async () => {
    if (!isHelpdeskAdmin && !currentUserId) return

    try {
      setLoadingEntityTypes(true)

      const filters: { userId?: string } = {}
      if (isHelpdeskAdmin) {
        if (userIdFilter) {
          filters.userId = userIdFilter
        }
      } else if (currentUserId) {
        filters.userId = currentUserId
      }

      const entityTypes = await helpdeskUserLogsService.listDistinctEntityTypes(filters)
      setAvailableEntityTypes(entityTypes)
    } catch (err) {
      console.error('Erro ao carregar tipos disponíveis:', err)
      setAvailableEntityTypes([])
    } finally {
      setLoadingEntityTypes(false)
    }
  }, [isHelpdeskAdmin, currentUserId, userIdFilter])

  useEffect(() => {
    void loadAvailableEntityTypes()
  }, [loadAvailableEntityTypes])

  useEffect(() => {
    if (entityTypeFilter !== 'all' && !availableEntityTypes.includes(entityTypeFilter)) {
      setEntityTypeFilter('all')
    }
  }, [availableEntityTypes, entityTypeFilter])

  const loadLogs = useCallback(
    async (pageOverride?: number) => {
      const page = pageOverride ?? currentPage

      try {
        setLoading(true)
        setError(null)

        const filters: Parameters<typeof helpdeskUserLogsService.getLogs>[0] = {
          page,
          limit: ITEMS_PER_PAGE,
        }

        if (isHelpdeskAdmin) {
          if (userIdFilter) {
            filters.userId = userIdFilter
          }
        } else if (currentUserId) {
          filters.userId = currentUserId
        }

        if (entityTypeFilter !== 'all') {
          filters.entityType = entityTypeFilter
        }

        if (dateFrom) {
          filters.dateFrom = localCalendarDayStartIso(dateFrom)
        }

        if (dateTo) {
          filters.dateTo = localCalendarDayEndIso(dateTo)
        }

        if (searchTerm) {
          filters.search = searchTerm
        }

        const result = await helpdeskUserLogsService.getLogs(filters)
        setLogs(result.logs)
        setTotal(result.total)

        const entityTypesFromResults = Array.from(
          new Set(result.logs.map((log) => log.entity_type).filter(Boolean))
        )
        if (entityTypesFromResults.length > 0) {
          setAvailableEntityTypes((prev) => {
            const merged = Array.from(new Set([...prev, ...entityTypesFromResults]))
            return merged.sort((a, b) => a.localeCompare(b, 'pt-BR'))
          })
        }
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Erro ao carregar logs'
        setError(message)
      } finally {
        setLoading(false)
      }
    },
    [
      currentPage,
      isHelpdeskAdmin,
      currentUserId,
      userIdFilter,
      entityTypeFilter,
      dateFrom,
      dateTo,
      searchTerm,
    ]
  )

  const handleBuscar = () => {
    setHasSearched(true)
    setCurrentPage(1)
    setError(null)
    void loadLogs(1)
  }

  const handleLimparFiltros = () => {
    const dates = getLastMonthDates()
    setUserIdFilter('')
    setEntityTypeFilter('all')
    setDateFrom(dates.inicio)
    setDateTo(dates.fim)
    setSearchTerm('')
    setCurrentPage(1)
    setHasSearched(false)
    setLogs([])
    setTotal(0)
    setError(null)
  }

  const toggleSort = (key: string) => {
    if (sortBy === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(key as typeof sortBy)
      setSortOrder('asc')
    }
  }

  const sortedLogs = useMemo(() => {
    const copy = [...logs]
    return copy.sort((a, b) => {
      let aValue: string | number
      let bValue: string | number

      switch (sortBy) {
        case 'created_at':
          aValue = new Date(a.created_at).getTime()
          bValue = new Date(b.created_at).getTime()
          break
        case 'user_name':
          aValue = (a.profiles?.name || '').toLowerCase()
          bValue = (b.profiles?.name || '').toLowerCase()
          break
        case 'action':
          aValue = translateAction(a.action).toLowerCase()
          bValue = translateAction(b.action).toLowerCase()
          break
        case 'entity_type':
          aValue = (a.entity_type || '').toLowerCase()
          bValue = (b.entity_type || '').toLowerCase()
          break
        case 'description':
          aValue = (a.description || '').toLowerCase()
          bValue = (b.description || '').toLowerCase()
          break
        default:
          aValue = a.created_at
          bValue = b.created_at
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1
      return 0
    })
  }, [logs, sortBy, sortOrder])

  const totalPages = Math.ceil(total / ITEMS_PER_PAGE)
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE
  const endIndex = Math.min(currentPage * ITEMS_PER_PAGE, total)

  const handlePreviousPage = () => {
    if (currentPage > 1 && hasSearched) {
      const p = currentPage - 1
      setCurrentPage(p)
      void loadLogs(p)
    }
  }

  const handleNextPage = () => {
    if (currentPage < totalPages && hasSearched) {
      const p = currentPage + 1
      setCurrentPage(p)
      void loadLogs(p)
    }
  }

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages && hasSearched) {
      setCurrentPage(page)
      void loadLogs(page)
    }
  }

  const openDetailModal = (log: HelpdeskLogWithUser) => {
    setSelectedLog(log)
    setIsDetailModalOpen(true)
  }

  const closeDetailModal = () => {
    setSelectedLog(null)
    setIsDetailModalOpen(false)
  }

  return (
    <div className="flex-1 flex flex-col overflow-y-auto">
      <div className="px-6 py-4 space-y-4">
        {!isHelpdeskAdmin && (
          <div className="rounded-lg border border-amber-200 dark:border-amber-900/40 bg-amber-50 dark:bg-amber-950/20 px-6 py-3">
            <Text size="sm" className="text-amber-800 dark:text-amber-200">
              Você está visualizando apenas os seus próprios registros de atividade.
            </Text>
          </div>
        )}

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center mb-4">
            <FunnelIcon className="h-5 w-5 text-gray-500 dark:text-gray-400 mr-2" />
            <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
              Filtros
            </Heading>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Buscar na descrição
              </label>
              <div className="relative">
                <Input
                  type="text"
                  placeholder="Digite para buscar..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              </div>
            </div>

            {isHelpdeskAdmin && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Usuário
                </label>
                <Select value={userIdFilter} onChange={(e) => setUserIdFilter(e.target.value)}>
                  <option value="">Todos os usuários</option>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name || user.id}
                    </option>
                  ))}
                </Select>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Tipo
              </label>
              <Select
                value={entityTypeFilter}
                onChange={(e) => setEntityTypeFilter(e.target.value)}
                disabled={loadingEntityTypes}
              >
                <option value="all">Todos os tipos</option>
                {availableEntityTypes.map((entityType) => (
                  <option key={entityType} value={entityType}>
                    {entityType}
                  </option>
                ))}
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Data Inicial
              </label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Data Final
              </label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <Button variant="solid" size="sm" onClick={handleBuscar} disabled={loading}>
              {loading ? 'Buscando...' : 'Buscar'}
            </Button>
            <Button variant="outline" size="sm" onClick={handleLimparFiltros} disabled={loading}>
              <XMarkIcon className="h-4 w-4 mr-2 inline" />
              Limpar Filtros
            </Button>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : error ? (
            <div className="p-8 text-center">
              <Text className="text-red-600 dark:text-red-400">{error}</Text>
            </div>
          ) : logs.length === 0 ? (
            <div className="p-8 text-center">
              <Text color="muted">
                {!hasSearched
                  ? 'Ajuste os filtros e clique em Buscar para carregar os registros.'
                  : 'Nenhum log encontrado.'}
              </Text>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <SortableTableHeader
                        sortKey="created_at"
                        currentSort={sortBy}
                        sortOrder={sortOrder}
                        onSort={toggleSort}
                      >
                        Data/Hora
                      </SortableTableHeader>
                      <SortableTableHeader
                        sortKey="user_name"
                        currentSort={sortBy}
                        sortOrder={sortOrder}
                        onSort={toggleSort}
                      >
                        Usuário
                      </SortableTableHeader>
                      <SortableTableHeader
                        sortKey="action"
                        currentSort={sortBy}
                        sortOrder={sortOrder}
                        onSort={toggleSort}
                      >
                        Ação
                      </SortableTableHeader>
                      <SortableTableHeader
                        sortKey="entity_type"
                        currentSort={sortBy}
                        sortOrder={sortOrder}
                        onSort={toggleSort}
                      >
                        Tipo
                      </SortableTableHeader>
                      <SortableTableHeader
                        sortKey="description"
                        currentSort={sortBy}
                        sortOrder={sortOrder}
                        onSort={toggleSort}
                      >
                        Descrição
                      </SortableTableHeader>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {sortedLogs.map((log) => (
                      <tr
                        key={log.id}
                        onClick={() => openDetailModal(log)}
                        className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          {formatDate(log.created_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          {log.profiles?.name || log.user_id}
                        </td>
                        <td className="px-6 py-4 text-sm whitespace-pre-wrap break-words max-w-xs">
                          <span className="px-2 inline-block text-xs leading-5 font-semibold rounded-full bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200 whitespace-pre-wrap break-words">
                            {translateAction(log.action)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          {log.entity_type}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100 max-w-md whitespace-pre-wrap break-words">
                          {log.description}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <Text size="sm" className="text-gray-700 dark:text-gray-300">
                      Mostrando {startIndex + 1} a {endIndex} de {total} registros
                    </Text>

                    <div className="flex items-center space-x-2">
                      <button
                        type="button"
                        onClick={handlePreviousPage}
                        disabled={currentPage === 1}
                        className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                      >
                        Anterior
                      </button>

                      <div className="flex space-x-1">
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          let pageNumber: number
                          if (totalPages <= 5) {
                            pageNumber = i + 1
                          } else if (currentPage <= 3) {
                            pageNumber = i + 1
                          } else if (currentPage >= totalPages - 2) {
                            pageNumber = totalPages - 4 + i
                          } else {
                            pageNumber = currentPage - 2 + i
                          }

                          return (
                            <button
                              key={pageNumber}
                              type="button"
                              onClick={() => handlePageChange(pageNumber)}
                              className={`px-3 py-2 text-sm font-medium rounded-md ${
                                currentPage === pageNumber
                                  ? 'bg-primary-600 text-white'
                                  : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
                              }`}
                            >
                              {pageNumber}
                            </button>
                          )
                        })}
                      </div>

                      <button
                        type="button"
                        onClick={handleNextPage}
                        disabled={currentPage === totalPages}
                        className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                      >
                        Próximo
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {isDetailModalOpen && selectedLog && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={closeDetailModal}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                Detalhes do Log
              </Heading>
              <Button variant="ghost" size="sm" onClick={closeDetailModal} className="p-2">
                <XMarkIcon className="h-5 w-5" />
              </Button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <Text size="sm" className="text-gray-500 dark:text-gray-400 mb-1">
                  ID do Registro
                </Text>
                <Text className="text-gray-900 dark:text-white font-mono text-sm">
                  {selectedLog.id}
                </Text>
              </div>

              <div>
                <Text size="sm" className="text-gray-500 dark:text-gray-400 mb-1">
                  Data/Hora
                </Text>
                <Text className="text-gray-900 dark:text-white">
                  {formatDate(selectedLog.created_at)}
                </Text>
              </div>

              <div>
                <Text size="sm" className="text-gray-500 dark:text-gray-400 mb-1">
                  Usuário
                </Text>
                <Text className="text-gray-900 dark:text-white">
                  {selectedLog.profiles?.name || 'Usuário desconhecido'}
                </Text>
                <Text size="sm" className="text-gray-500 dark:text-gray-400 font-mono mt-1">
                  {selectedLog.user_id}
                </Text>
              </div>

              <div>
                <Text size="sm" className="text-gray-500 dark:text-gray-400 mb-1">
                  Ação
                </Text>
                <Text className="text-gray-900 dark:text-white whitespace-pre-wrap break-words">
                  {translateAction(selectedLog.action)}
                </Text>
              </div>

              <div>
                <Text size="sm" className="text-gray-500 dark:text-gray-400 mb-1">
                  Tipo de Entidade
                </Text>
                <Text className="text-gray-900 dark:text-white">{selectedLog.entity_type}</Text>
              </div>

              {selectedLog.entity_id && (
                <div>
                  <Text size="sm" className="text-gray-500 dark:text-gray-400 mb-1">
                    ID da Entidade
                  </Text>
                  <Text className="text-gray-900 dark:text-white font-mono text-sm">
                    {selectedLog.entity_id}
                  </Text>
                </div>
              )}

              <div>
                <Text size="sm" className="text-gray-500 dark:text-gray-400 mb-1">
                  Descrição
                </Text>
                <Text className="text-gray-900 dark:text-white whitespace-pre-wrap break-words">
                  {selectedLog.description}
                </Text>
              </div>

              {selectedLog.page_url && (
                <div>
                  <Text size="sm" className="text-gray-500 dark:text-gray-400 mb-1">
                    URL da Página
                  </Text>
                  <Text className="text-gray-900 dark:text-white break-all">
                    {selectedLog.page_url}
                  </Text>
                </div>
              )}

              {selectedLog.ip_address && (
                <div>
                  <Text size="sm" className="text-gray-500 dark:text-gray-400 mb-1">
                    Endereço IP
                  </Text>
                  <Text className="text-gray-900 dark:text-white">{selectedLog.ip_address}</Text>
                </div>
              )}

              {selectedLog.metadata && (
                <div>
                  <Text size="sm" className="text-gray-500 dark:text-gray-400 mb-1">
                    Metadados
                  </Text>
                  <pre className="bg-gray-100 dark:bg-gray-900 p-4 rounded text-sm overflow-x-auto">
                    {JSON.stringify(selectedLog.metadata, null, 2)}
                  </pre>
                </div>
              )}

              {selectedLog.user_agent && (
                <div>
                  <Text size="sm" className="text-gray-500 dark:text-gray-400 mb-1">
                    User Agent
                  </Text>
                  <Text className="text-gray-900 dark:text-white text-sm break-all">
                    {selectedLog.user_agent}
                  </Text>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-700">
              <Button
                onClick={closeDetailModal}
                style={{ backgroundColor: BRAND_COLOR }}
                className="w-full"
              >
                Fechar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default PortaisAtendimentoUserActivityLogsTab
